import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Badge } from './ui/badge';
import { orderSourceLabel } from '../lib/orderSourceLabel';
import { formatTimeMexico } from '../lib/dateMexico';

const PAYMENT_STATUS_LABELS = {
  paid: 'Pagado',
  unpaid: 'No pagado',
  awaiting_receipt: 'Pendiente comprobante',
  pending_verification: 'Comprobante por revisar',
  pay_on_delivery: 'Paga al entregar',
};

const PAYMENT_METHOD_LABELS = {
  mercadopago: 'Mercado Pago',
  transfer: 'Transferencia',
  cash_on_delivery: 'Contra entrega',
  store_cash: 'Efectivo en sucursal',
};

const STATUS_LABELS = {
  pending_payment: 'Pago pendiente',
  pending_review: 'Por revisar',
  proposed_delivery: 'Horario propuesto',
  accepted: 'Aceptado',
  preparing: 'En preparación',
  awaiting_extras: 'En caja (complementos)',
  ready: 'Listo para recoger',
  dispatched: 'En camino',
  delivered: 'Entregado',
  rejected: 'Rechazado',
  cancelled: 'Cancelado',
};

export default function OrderSummaryDialog({ open, onOpenChange, order }) {
  if (!order) return null;

  const lines = Array.isArray(order.lines) ? order.lines : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Resumen — pedido #{order.order_number}</DialogTitle>
          <DialogDescription>
            {order.customer_name || 'Cliente'} · {orderSourceLabel(order.source)}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-2 text-sm">
          <div>
            <span className="text-muted-foreground">Estado</span>
            <p className="font-medium">{STATUS_LABELS[order.status] || order.status}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Pago</span>
            <p className="font-medium">
              {PAYMENT_STATUS_LABELS[order.payment_status] || order.payment_status}
              {' · '}
              {PAYMENT_METHOD_LABELS[order.payment_method] || order.payment_method}
            </p>
          </div>
          <div>
            <span className="text-muted-foreground">Teléfono</span>
            <p>{order.customer_phone || '—'}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Tipo</span>
            <p>{order.delivery_type === 'pickup' ? 'Recoger en sucursal' : 'Domicilio'}</p>
          </div>
          {order.estimated_ready_at ? (
            <div>
              <span className="text-muted-foreground">ETA</span>
              <p>{formatTimeMexico(order.estimated_ready_at)}</p>
            </div>
          ) : null}
          {order.sold_by_name ? (
            <div>
              <span className="text-muted-foreground">Vendido por</span>
              <p>{order.sold_by_name}</p>
            </div>
          ) : null}
        </div>

        <div className="border rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-2">Producto</th>
                <th className="text-right p-2">Cant.</th>
                <th className="text-right p-2">Importe</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((ln, i) => (
                <tr key={`${ln.menu_item_id}-${i}`} className="border-t">
                  <td className="p-2">
                    {ln.name}
                    {ln.line_type === 'extra' ? (
                      <Badge variant="outline" className="ml-1 text-[10px]">
                        extra
                      </Badge>
                    ) : null}
                  </td>
                  <td className="p-2 text-right">{ln.qty}</td>
                  <td className="p-2 text-right tabular-nums">
                    ${Number(ln.line_total || 0).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="text-sm space-y-1 border-t pt-2">
          <div className="flex justify-between">
            <span>Subtotal</span>
            <span className="tabular-nums">${Number(order.subtotal || 0).toFixed(2)}</span>
          </div>
          {Number(order.delivery_fee) > 0 ? (
            <div className="flex justify-between">
              <span>Envío</span>
              <span className="tabular-nums">${Number(order.delivery_fee).toFixed(2)}</span>
            </div>
          ) : null}
          {Number(order.promo_discount) > 0 ? (
            <div className="flex justify-between text-green-700">
              <span>Descuento{order.promo_code ? ` (${order.promo_code})` : ''}</span>
              <span className="tabular-nums">-${Number(order.promo_discount).toFixed(2)}</span>
            </div>
          ) : null}
          <div className="flex justify-between font-bold text-base pt-1">
            <span>Total</span>
            <span className="tabular-nums">${Number(order.total || 0).toFixed(2)}</span>
          </div>
        </div>

        {order.notes || order.admin_notes ? (
          <p className="text-xs text-muted-foreground">
            Notas: {order.notes || order.admin_notes}
          </p>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
